/**
 * Écrans client restants : expéditeurs, clés API, facturation.
 *
 * Les listes de diffusion ont leur propre fichier : elles portent la base
 * légale des envois, ce qui en fait bien plus qu'un écran de saisie.
 */
import { useEffect, useState } from 'react';
import {
  api,
  type CleApi,
  type Expediteur,
  type Facturation as DonneesFacturation,
  type Facture,
  type FactureDetaillee,
} from '../../api/client';
import { usePlateforme } from '../../lib/plateforme';
import {
  Bandeau,
  Carte,
  Chargement,
  EtatVide,
  MessageErreur,
  MessageSucces,
  PuceLibre,
} from '../../components/Ui';
import {
  formaterDate,
  formaterDateCivile,
  formaterFcfa,
  formaterMois,
  formaterMontantSigne,
  formaterNombre,
  libelleEcriture,
} from '../../lib/format';

// --- Expéditeurs et modèles -------------------------------------------------

const ETAT_EXPEDITEUR = {
  approved: { ton: 'ok' as const, libelle: 'Validé' },
  pending: { ton: 'attente' as const, libelle: 'En attente opérateur' },
  rejected: { ton: 'echec' as const, libelle: 'Refusé' },
};

export function Expediteurs() {
  const [expediteurs, setExpediteurs] = useState<Expediteur[] | null>(null);
  const [valeur, setValeur] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);

  const recharger = () =>
    api
      .expediteurs()
      .then(({ data }) => setExpediteurs(data))
      .catch(setErreur);

  useEffect(() => {
    void recharger();
  }, []);

  async function demander() {
    setErreur(null);
    try {
      await api.creerExpediteur(valeur.trim());
      setValeur('');
      await recharger();
    } catch (e) {
      setErreur(e);
    }
  }

  const trop = valeur.trim().length > 11;

  return (
    <>
      <MessageErreur erreur={erreur} />

      <Bandeau titre="Un expéditeur doit être validé par l’opérateur avant tout envoi en production.">
        La norme SMPP limite un expéditeur alphanumérique à <strong>11 caractères</strong>. Le bac à
        sable accepte n’importe quel expéditeur bien formé — c’est précisément à cela qu’il sert.
      </Bandeau>

      <Carte titre="Demander un expéditeur">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="champ" style={{ flex: '1 1 260px' }}>
            <span>Expéditeur souhaité</span>
            <input
              type="text"
              value={valeur}
              onChange={(e) => setValeur(e.target.value)}
              maxLength={20}
              placeholder="MABANQUE"
            />
            <div className="aide" style={{ color: trop ? 'var(--rouge)' : undefined }}>
              {valeur.trim().length} / 11 caractères
              {trop && ' — trop long, l’opérateur rejettera sans message d’erreur exploitable.'}
            </div>
          </label>
          <button className="primaire" onClick={demander} disabled={!valeur.trim() || trop}>
            Soumettre à validation
          </button>
        </div>
      </Carte>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Vos expéditeurs" sansCorps>
          {expediteurs === null ? (
            <Chargement />
          ) : expediteurs.length === 0 ? (
            <EtatVide titre="Aucun expéditeur déclaré." />
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 620 }}>
                <thead>
                  <tr>
                    <th>Expéditeur</th>
                    <th className="droite">Longueur</th>
                    <th>État</th>
                    <th>Motif</th>
                    <th>Demandé le</th>
                  </tr>
                </thead>
                <tbody>
                  {expediteurs.map((e) => {
                    const etat = ETAT_EXPEDITEUR[e.status];
                    return (
                      <tr key={e.id}>
                        <td>
                          <span className="etiquette-sid">{e.value}</span>
                        </td>
                        <td className="nombre">{e.value.length}</td>
                        <td>
                          <PuceLibre ton={etat.ton}>{etat.libelle}</PuceLibre>
                        </td>
                        <td style={{ color: 'var(--muet)' }}>{e.reason ?? '—'}</td>
                        <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                          {formaterDate(e.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>
    </>
  );
}

// --- Clés API ---------------------------------------------------------------

export function ClesApi() {
  const plateforme = usePlateforme();
  const [cles, setCles] = useState<CleApi[] | null>(null);
  const [nom, setNom] = useState('');
  const [nouvelle, setNouvelle] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  const recharger = () =>
    api
      .clesApi()
      .then(({ data }) => setCles(data))
      .catch(setErreur);

  useEffect(() => {
    void recharger();
  }, []);

  async function creer() {
    setErreur(null);
    try {
      const cle = await api.creerCleApi(nom.trim());
      setNouvelle(cle.key ?? null);
      setNom('');
      await recharger();
    } catch (e) {
      setErreur(e);
    }
  }

  async function revoquer(id: string) {
    setErreur(null);
    try {
      await api.revoquerCleApi(id);
      await recharger();
    } catch (e) {
      setErreur(e);
    }
  }

  return (
    <>
      <MessageErreur erreur={erreur} />

      {nouvelle && (
        <MessageSucces>
          <div>Clé créée. Copiez-la maintenant : elle ne sera plus jamais affichée.</div>
          <code
            className="mono"
            style={{ display: 'block', marginTop: 8, userSelect: 'all', fontSize: 13 }}
          >
            {nouvelle}
          </code>
        </MessageSucces>
      )}

      <Bandeau
        ton={plateforme.mode === 'production' ? 'rouge' : 'orange'}
        titre={`Les clés de cette plateforme commencent par ${plateforme.key_prefix}`}
      >
        {plateforme.mode === 'production' ? (
          <>
            Une clé <span className="mono">ik_live_</span> atteint l’opérateur et débite votre
            solde. Vos clés de recette vivent sur la plateforme de test, avec leur propre compte —
            elles ne fonctionnent pas ici, et c’est voulu.
          </>
        ) : (
          <>
            Une clé <span className="mono">ik_test_</span> n’atteint jamais l’opérateur et ne débite
            rien. Elle est refusée par la plateforme de production : une clé de recette laissée dans
            une configuration de production échoue bruyamment au lieu d’envoyer par erreur.
          </>
        )}
      </Bandeau>

      <Carte titre="Créer une clé">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="champ" style={{ flex: '1 1 240px' }}>
            <span>Nom</span>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Serveur applicatif"
            />
          </label>
          <button className="primaire" onClick={creer} disabled={!nom.trim()}>
            Créer
          </button>
        </div>
      </Carte>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Clés existantes" sansCorps>
          {cles === null ? (
            <Chargement />
          ) : cles.length === 0 ? (
            <EtatVide titre="Aucune clé pour l’instant." />
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 720 }}>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Environnement</th>
                    <th>Clé</th>
                    <th>État</th>
                    <th>Dernier usage</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cles.map((cle) => (
                    <tr key={cle.id}>
                      <td>{cle.name}</td>
                      <td>
                        <PuceLibre ton={cle.environment === 'live' ? 'ok' : 'neutre'}>
                          {cle.environment === 'live' ? 'Production' : 'Bac à sable'}
                        </PuceLibre>
                      </td>
                      <td className="mono" style={{ fontSize: 12.5 }}>
                        {cle.prefix}••••••••{cle.last4}
                      </td>
                      <td>
                        {cle.status === 'active' ? (
                          <PuceLibre ton="succes">Active</PuceLibre>
                        ) : (
                          <PuceLibre ton="neutre">Révoquée</PuceLibre>
                        )}
                      </td>
                      <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                        {cle.last_used_at ? formaterDate(cle.last_used_at) : 'Jamais'}
                      </td>
                      <td className="droite">
                        {cle.status === 'active' && (
                          <button className="secondaire petit" onClick={() => revoquer(cle.id)}>
                            Révoquer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>
    </>
  );
}

// --- Crédits et facturation -------------------------------------------------

export function Facturation() {
  const [donnees, setDonnees] = useState<DonneesFacturation | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  useEffect(() => {
    api.facturation().then(setDonnees).catch(setErreur);
  }, []);

  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!donnees) return <Chargement quoi="Chargement de la facturation…" />;

  const tva = donnees.vat_rate_bps / 100;

  return (
    <>
      {donnees.missing_mentions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Bandeau ton="orange">
            Mentions obligatoires manquantes : {donnees.missing_mentions.join(', ')}. Aucune facture
            ne peut être émise tant qu’elles ne sont pas renseignées — une facture OHADA sans NIF ni
            RCCM n’est pas opposable.
          </Bandeau>
        </div>
      )}

      <div className="grille auto-210">
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">Solde</div>
            <div className="valeur" style={{ color: 'var(--vert)' }}>
              {formaterNombre(donnees.balance_xaf)}
            </div>
            <div className="note">FCFA</div>
          </div>
        </div>
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">Disponible à l’envoi</div>
            <div className="valeur">{formaterNombre(donnees.available_xaf)}</div>
            <div className="note">FCFA</div>
          </div>
        </div>
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">Taux de TVA</div>
            <div className="valeur">{tva} %</div>
            <div className="note">appliqué au total mensuel, jamais message par message</div>
          </div>
        </div>
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">Seuil d’alerte</div>
            <div className="valeur">{formaterNombre(donnees.low_balance_threshold_xaf)}</div>
            <div className="note">FCFA</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Consommation par mois" sansCorps>
          {donnees.months.length === 0 ? (
            <EtatVide titre="Aucune consommation facturable sur les six derniers mois." />
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 680 }}>
                <thead>
                  <tr>
                    <th>Période</th>
                    <th className="droite">Messages</th>
                    <th className="droite">Segments facturés</th>
                    <th className="droite">Total hors taxes</th>
                    <th className="droite">TVA {tva} %</th>
                    <th className="droite">Total TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {donnees.months.map((m) => (
                    <tr key={m.month}>
                      <td>{formaterMois(m.month)}</td>
                      <td className="nombre">{formaterNombre(m.messages)}</td>
                      <td className="nombre">{formaterNombre(m.segments)}</td>
                      <td className="nombre">{formaterFcfa(m.total_ht_xaf)}</td>
                      <td className="nombre">{formaterFcfa(m.vat_xaf)}</td>
                      <td className="nombre" style={{ fontWeight: 500, color: 'var(--rouge)' }}>
                        {formaterFcfa(m.total_ttc_xaf)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="corps" style={{ paddingTop: 0 }}>
            <div className="aide" style={{ marginTop: 0 }}>
              Consommation constatée. Les messages filtrés par la liste STOP, rejetés à la
              validation ou recrédités après non-remise n’y figurent pas, et ne sont pas facturés.
            </div>
          </div>
        </Carte>
      </div>

      <div style={{ marginTop: 16 }}>
        <ListeFactures factures={donnees.invoices} />
      </div>

      <div className="rangee" style={{ marginTop: 16 }}>
        <div className="large">
          <Carte titre="Grand livre" sansCorps>
            {donnees.entries.length === 0 ? (
              <EtatVide titre="Aucune écriture." />
            ) : (
              <div className="tableau-conteneur">
                <table style={{ minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Libellé</th>
                      <th className="droite">Montant</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donnees.entries.map((e) => (
                      <tr key={e.id}>
                        <td>{libelleEcriture(e.type)}</td>
                        <td style={{ color: 'var(--muet)' }}>{e.description ?? '—'}</td>
                        <td className={`nombre montant ${e.amount_xaf < 0 ? 'debit' : 'credit'}`}>
                          {formaterMontantSigne(e.amount_xaf)}
                        </td>
                        <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                          {formaterDate(e.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Carte>
        </div>

        <div className="etroit">
          <Carte titre="Mentions de facturation">
            <dl className="definitions">
              <dt>Raison sociale</dt>
              <dd>{donnees.legal.legal_name ?? '— à renseigner'}</dd>
              <dt>NIF</dt>
              <dd>{donnees.legal.nif ?? '— à renseigner'}</dd>
              <dt>RCCM</dt>
              <dd>{donnees.legal.rccm ?? '— à renseigner'}</dd>
              <dt>Adresse</dt>
              <dd>{donnees.legal.address ?? '— à renseigner'}</dd>
            </dl>
            <div className="aide">
              Ces mentions sont obligatoires sur une facture OHADA, au même titre que le numéro
              séquentiel sans trou et la TVA affichée explicitement.
            </div>
          </Carte>
        </div>
      </div>
    </>
  );
}

// --- Factures OHADA ---------------------------------------------------------

const ASPECT_FACTURE: Record<
  Facture['status'],
  { libelle: string; ton: 'ok' | 'attente' | 'neutre' | 'succes' | 'echec' }
> = {
  draft: { libelle: 'brouillon', ton: 'neutre' },
  issued: { libelle: 'émise', ton: 'attente' },
  paid: { libelle: 'réglée', ton: 'succes' },
  cancelled: { libelle: 'annulée', ton: 'echec' },
};

/**
 * Libellé du mois facturé. Les bornes sont stockées en heure du Gabon (UTC+1) :
 * lues en UTC, le 1er août à 00 h y devient le 31 juillet à 23 h, et le mois
 * affiché serait faux d'une unité.
 */
function libellePeriode(debut: string, _fin: string): string {
  return new Date(debut).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Africa/Libreville',
  });
}

export function ListeFactures({ factures }: { factures: Facture[] }) {
  const [ouverte, setOuverte] = useState<FactureDetaillee | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  async function ouvrir(id: string) {
    setErreur(null);
    try {
      setOuverte(await api.facture(id));
    } catch (e) {
      setErreur(e);
    }
  }

  return (
    <>
      <Carte titre="Factures" sansCorps>
        {erreur ? (
          <div className="corps">
            <MessageErreur erreur={erreur} />
          </div>
        ) : null}
        {factures.length === 0 ? (
          <EtatVide titre="Aucune facture émise.">
            <p>
              Les factures sont établies après la clôture du mois : un message émis hier peut encore
              être recrédité aujourd’hui.
            </p>
          </EtatVide>
        ) : (
          <div className="tableau-conteneur">
            <table style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Période</th>
                  <th>État</th>
                  <th className="droite">Hors taxes</th>
                  <th className="droite">TVA</th>
                  <th className="droite">Total TTC</th>
                  <th>Échéance</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {factures.map((f) => {
                  const aspect = ASPECT_FACTURE[f.status];
                  return (
                    <tr key={f.id}>
                      <td className="mono">
                        {f.number ?? '—'}
                        {f.kind === 'credit_note' && (
                          <span style={{ marginLeft: 6 }}>
                            <PuceLibre ton="neutre">avoir</PuceLibre>
                          </span>
                        )}
                      </td>
                      <td>{libellePeriode(f.period_start, f.period_end)}</td>
                      <td>
                        <PuceLibre ton={aspect.ton}>{aspect.libelle}</PuceLibre>
                      </td>
                      <td className="nombre">{formaterFcfa(f.total_ht_xaf)}</td>
                      <td className="nombre">{formaterFcfa(f.total_vat_xaf)}</td>
                      <td className="nombre" style={{ fontWeight: 500 }}>
                        {formaterFcfa(f.total_ttc_xaf)}
                      </td>
                      <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                        {f.due_at ? formaterDateCivile(f.due_at) : '—'}
                      </td>
                      <td className="droite">
                        <button type="button" className="secondaire" onClick={() => ouvrir(f.id)}>
                          Consulter
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {ouverte && <FactureImprimable facture={ouverte} onFermer={() => setOuverte(null)} />}
    </>
  );
}

/**
 * Facture au format OHADA, imprimable.
 *
 * Toutes les mentions obligatoires y figurent, et la TVA apparaît séparément :
 * un total TTC seul ne satisfait pas l'obligation.
 */
export function FactureImprimable({
  facture,
  onFermer,
}: {
  facture: FactureDetaillee;
  onFermer: () => void;
}) {
  const taux = facture.vat_rate_bps / 100;

  return (
    <div className="voile" role="dialog" aria-label={`Facture ${facture.number ?? 'brouillon'}`}>
      <div className="panneau facture">
        <div className="panneau-tete sans-impression">
          <b>
            {facture.kind === 'credit_note' ? 'Avoir' : 'Facture'} {facture.number ?? '(brouillon)'}
          </b>
          <div className="rangee-boutons">
            <button type="button" className="secondaire" onClick={() => window.print()}>
              Imprimer
            </button>
            <button type="button" className="secondaire" onClick={onFermer}>
              Fermer
            </button>
          </div>
        </div>

        <div className="panneau-corps document">
          <div className="document-entete">
            <div>
              <div className="document-emetteur">{facture.issuer.legal_name}</div>
              <div className="document-mentions">
                {facture.issuer.legal_form} au capital de {formaterFcfa(facture.issuer.capital_xaf)}
                <br />
                NIF {facture.issuer.nif} — RCCM {facture.issuer.rccm}
                <br />
                {facture.issuer.address}
                {facture.issuer.bank && (
                  <>
                    <br />
                    {facture.issuer.bank}
                  </>
                )}
              </div>
            </div>
            <div className="document-numero">
              <div className="intitule">
                {facture.kind === 'credit_note' ? 'Avoir n°' : 'Facture n°'}
              </div>
              <div className="valeur">{facture.number ?? '(non émise)'}</div>
              <div className="document-mentions">
                Émise le {facture.issued_at ? formaterDateCivile(facture.issued_at) : '—'}
                <br />
                Échéance : {facture.due_at ? formaterDateCivile(facture.due_at) : '—'}
                <br />
                Période : {libellePeriode(facture.period_start, facture.period_end)}
              </div>
            </div>
          </div>

          <div className="document-client">
            <div className="intitule">Client</div>
            <div className="document-mentions">
              <b>{facture.customer.legal_name ?? facture.customer.name}</b>
              <br />
              {facture.customer.nif && <>NIF {facture.customer.nif}</>}
              {facture.customer.rccm && <> — RCCM {facture.customer.rccm}</>}
              <br />
              {facture.customer.address ?? '—'}
            </div>
          </div>

          <div className="tableau-conteneur">
            <table className="document-lignes">
              <thead>
                <tr>
                  <th>Désignation</th>
                  <th className="droite">Quantité</th>
                  <th className="droite">P.U. HT</th>
                  <th className="droite">Montant HT</th>
                  <th className="droite">TVA {taux} %</th>
                </tr>
              </thead>
              <tbody>
                {facture.lines.map((ligne) => (
                  <tr key={ligne.position}>
                    <td>{ligne.designation}</td>
                    <td className="nombre">
                      {formaterNombre(ligne.quantity)} {ligne.unit}
                      {Math.abs(ligne.quantity) > 1 ? 's' : ''}
                    </td>
                    <td className="nombre">{formaterFcfa(ligne.unit_price_xaf)}</td>
                    <td className="nombre">{formaterFcfa(ligne.total_ht_xaf)}</td>
                    <td className="nombre">{formaterFcfa(ligne.vat_xaf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="document-totaux">
            <div>
              <span>Total hors taxes</span>
              <b className="nombre">{formaterFcfa(facture.total_ht_xaf)}</b>
            </div>
            <div>
              <span>TVA {taux} %</span>
              <b className="nombre">{formaterFcfa(facture.total_vat_xaf)}</b>
            </div>
            <div className="total-ttc">
              <span>Total toutes taxes comprises</span>
              <b className="nombre">{formaterFcfa(facture.total_ttc_xaf)}</b>
            </div>
          </div>

          {facture.payment_terms && (
            <div className="document-conditions">{facture.payment_terms}</div>
          )}
          {facture.status === 'cancelled' && facture.cancel_reason && (
            <div className="document-conditions" style={{ color: 'var(--rouge)' }}>
              Facture annulée. Motif : {facture.cancel_reason}
            </div>
          )}
          {facture.status === 'paid' && (
            <div className="document-conditions" style={{ color: 'var(--vert)' }}>
              Réglée le {facture.paid_at ? formaterDateCivile(facture.paid_at) : '—'}
              {facture.payment_reference && <> — référence {facture.payment_reference}</>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
